import { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ThemeContext = createContext();

export const THEMES = {
  DARK: 'dark',
  LIGHT: 'light',
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(THEMES.LIGHT); // Default to LIGHT
  const [isLoading, setIsLoading] = useState(true);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('eoc-theme');
    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      setThemeState(savedTheme);
      applyTheme(savedTheme);
    } else {
      applyTheme(THEMES.LIGHT); // Default to LIGHT mode
    }
    setIsLoading(false);
  }, []);

  // Fetch user preference from server if logged in
  useEffect(() => {
    const fetchUserTheme = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/api/user/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.theme && Object.values(THEMES).includes(data.theme)) {
            setThemeState(data.theme);
            applyTheme(data.theme);
            localStorage.setItem('eoc-theme', data.theme);
          }
        }
      } catch (error) {
        console.warn('Could not fetch user theme preference');
      }
    };

    fetchUserTheme();
  }, []);

  const applyTheme = (newTheme) => {
    const root = document.documentElement;
    root.setAttribute('data-theme', newTheme);
    
    // Also update body class for Tailwind dark mode
    if (newTheme === THEMES.LIGHT) {
      document.body.classList.remove('dark');
      document.body.classList.add('light');
    } else {
      document.body.classList.remove('light');
      document.body.classList.add('dark');
    }
  };

  const setTheme = async (newTheme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('eoc-theme', newTheme);

    // Save to server if logged in
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${API_URL}/api/user/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ theme: newTheme }),
        });
      } catch (error) {
        console.warn('Could not save theme preference to server');
      }
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
